/* Zaroori Jini OMS — Public Landing Page
 * Structured per Website Content Blueprint:
 *   Hero → Trusted Brands → Products (16) → Integrations (categorized) →
 *   Industries (15) → AI Command Center → Enterprise Features →
 *   Testimonials → Pricing Preview → FAQ → CTA → Footer
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ShoppingCart, Warehouse, Truck, RotateCcw, MessageSquare, DollarSign,
  BarChart3, Sparkles, Package, ShieldCheck, Zap, Globe, Boxes,
  Eye, EyeOff, ArrowRight, CheckCircle2, Users, HeadphonesIcon,
  Building2, User, Lock, Mail, TrendingUp, Brain, Bot, LineChart,
  FileText, Layers, Store, Factory, Shirt, Sparkle, Gem, Utensils,
  Heart, Cpu, Truck as TruckI, Hammer, Handshake, ShoppingBag, Ship,
  KeyRound, GitBranch, Radio, Rocket, Star, ChevronDown, Play,
} from "lucide-react";
import {
  RED, RED_DARK, INK, MUTED, CREAM, BORDER,
  Chip, Section, SectionTitle, PrimaryButton, GhostButton,
  FieldLabel, FieldInput, MarketingHeader, MarketingFooter,
} from "@/components/marketing/atoms";
import { PLANS } from "@/components/marketing/ROICalculator";

/* ============================ MARKETPLACE HUB (Hero graphic) ============================ */
function BrandBadge({ name }) {
  const style = { fontWeight: 800, fontSize: 12, textAlign: "center", padding: "0 6px", lineHeight: 1.1 };
  switch (name) {
    case "amazon":
      return (
        <div style={{ ...style, color: "#131A22", position: "relative" }}>
          <span style={{ fontFamily: "'Amazon Ember', system-ui, sans-serif", fontStyle: "italic" }}>amazon</span>
          <div style={{ width: 30, height: 6, borderTop: "1.5px solid #FF9900", borderRadius: "50%", margin: "1px auto 0", transform: "rotate(-8deg)" }} />
        </div>
      );
    case "nykaa":
      return <div style={{ ...style, color: "#FC2779", fontWeight: 900, letterSpacing: "0.02em", fontFamily: "Georgia, serif" }}>NY<span style={{ fontStyle: "italic" }}>K</span>AA</div>;
    case "ajio":
      return <div style={{ ...style, color: "#000", fontWeight: 900, letterSpacing: "0.06em" }}>AJIO</div>;
    case "tatacliq":
      return (
        <div style={{ ...style, color: "#DA1F26", fontWeight: 900, lineHeight: 1 }}>
          <div style={{ fontSize: 10 }}>TATA</div>
          <div style={{ fontSize: 11, letterSpacing: "0.02em" }}>CLiQ</div>
        </div>
      );
    case "flipkart":
      return (
        <div style={{ ...style, position: "relative" }}>
          <svg width="26" height="20" viewBox="0 0 26 20" style={{ display: "block", margin: "0 auto" }}>
            <rect x="4" y="4" width="18" height="14" fill="#F0B90B" />
            <rect x="4" y="4" width="18" height="4" fill="#2874F0" />
            <path d="M 8 12 L 12 12 M 8 15 L 14 15" stroke="#2874F0" strokeWidth="1" />
          </svg>
          <div style={{ color: "#2874F0", fontStyle: "italic", fontSize: 10, marginTop: 1 }}>Flipkart</div>
        </div>
      );
    case "myntra":
      return (
        <div style={{ ...style }}>
          <svg width="24" height="16" viewBox="0 0 24 16" style={{ display: "block", margin: "0 auto" }}>
            <path d="M 2 14 L 6 2 L 10 10 L 14 2 L 18 14" stroke="#F13AB1" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M 18 14 L 22 6" stroke="#F97316" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
          <div style={{ color: "#231F20", fontSize: 10, marginTop: 1, fontWeight: 700 }}>Myntra</div>
        </div>
      );
    case "meesho":
      return <div style={{ ...style, color: "#F43397", fontWeight: 800, fontStyle: "italic" }}>meesho</div>;
    case "blinkit":
      return <div style={{ ...style, color: "#F8CB46", background: "#0C831F", borderRadius: 4, padding: "3px 6px", fontWeight: 900 }}>blinkit</div>;
    default:
      return <div style={style}>{name}</div>;
  }
}

const HUB_SPOKES = [
  { name: "amazon", pos: { top: 0, left: "50%" } },
  { name: "nykaa", pos: { top: "18%", right: 0 } },
  { name: "ajio", pos: { top: "50%", right: "-4%" } },
  { name: "tatacliq", pos: { bottom: "18%", right: 0 } },
  { name: "flipkart", pos: { bottom: 0, left: "50%" } },
  { name: "blinkit", pos: { bottom: "18%", left: 0 } },
  { name: "myntra", pos: { top: "50%", left: "-4%" } },
  { name: "meesho", pos: { top: "18%", left: 0 } },
];

function MarketplaceHub() {
  return (
    <div style={{ position: "relative", width: "100%", maxWidth: 480, aspectRatio: "1 / 1", margin: "0 auto" }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, #E5E7EB 1px, transparent 1px)", backgroundSize: "16px 16px", opacity: 0.5, borderRadius: 12 }} />
      <div className="hub-ring" style={{ position: "absolute", inset: "16%", borderRadius: "50%", border: `1.5px dashed #CBD5E1` }} />
      <div className="hub-glow" style={{ position: "absolute", inset: "28%", borderRadius: "50%", background: "radial-gradient(circle, rgba(220,38,38,0.08) 0%, rgba(220,38,38,0) 70%)" }} />
      <div
        className="hub-center"
        style={{
          position: "absolute", top: "50%", left: "50%", width: 180, height: 180,
          background: "#fff", borderRadius: "50%",
          boxShadow: "0 20px 50px -15px rgba(15,23,42,0.18)",
          display: "grid", placeItems: "center", zIndex: 5,
        }}
      >
        <div style={{ textAlign: "center", padding: "0 14px" }}>
          <img
            src="https://customer-assets-cm19k8pv.emergentagent.net/job_scalable-fashion-oms/artifacts/es403apk_zaroori%20jini%20oms.png"
            alt="Zaroori Jini OMS"
            style={{ maxWidth: "88%", height: "auto", display: "block", margin: "0 auto" }}
            data-testid="hub-center-logo"
          />
          <div style={{ marginTop: 10, height: 1, width: 60, background: BORDER, margin: "10px auto 6px" }} />
          <div style={{ fontSize: 10, color: MUTED, letterSpacing: "0.06em", fontWeight: 600 }}>AI · UNIFIED · SCALABLE</div>
        </div>
      </div>
      {HUB_SPOKES.map((s, i) => (
        <div
          key={s.name}
          className="hub-spoke-wrap"
          style={{ position: "absolute", ...s.pos, animationDelay: `${i * 0.35}s` }}
        >
          <div
            className="hub-spoke"
            style={{
              width: 76, height: 76, background: "#fff", borderRadius: "50%",
              display: "grid", placeItems: "center",
              boxShadow: "0 10px 26px -10px rgba(15,23,42,0.22)",
              border: `1px solid ${BORDER}`,
              animationDelay: `${i * 0.35}s`,
            }}
          >
            <BrandBadge name={s.name} />
          </div>
        </div>
      ))}
      <div className="hub-pulse hub-pulse-1" />
      <div className="hub-pulse hub-pulse-2" />
      <style>{`
        .hub-ring { animation: hub-rotate 30s linear infinite; }
        @keyframes hub-rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .hub-glow { animation: hub-glow-pulse 4.5s ease-in-out infinite; }
        @keyframes hub-glow-pulse { 0%,100%{opacity:0.6;transform:scale(1);} 50%{opacity:1;transform:scale(1.08);} }
        .hub-center { transform: translate(-50%, -50%); animation: hub-center-pulse 3.4s ease-in-out infinite; }
        @keyframes hub-center-pulse {
          0%,100%{transform:translate(-50%,-50%) scale(1);box-shadow:0 20px 50px -15px rgba(15,23,42,0.18);}
          50%{transform:translate(-50%,-50%) scale(1.035);box-shadow:0 26px 60px -12px rgba(220,38,38,0.28);}
        }
        .hub-spoke-wrap { transform: translate(-50%, -50%); animation: hub-orbit 4s ease-in-out infinite; }
        @keyframes hub-orbit {
          0%,100%{transform:translate(-50%,-50%) translateY(0px) translateX(0px);}
          50%{transform:translate(-50%,-50%) translateY(0px) translateX(4px);}
          25%{transform:translate(-50%,-50%) translateY(-10px) translateX(2px);}
          75%{transform:translate(-50%,-50%) translateY(10px) translateX(2px);}
        }
        .hub-spoke { animation: hub-spoke-tilt 4.2s ease-in-out infinite; transition: transform 300ms ease, box-shadow 300ms ease; }
        .hub-spoke:hover { transform: scale(1.15) !important; box-shadow: 0 18px 40px -10px rgba(220,38,38,0.35) !important; z-index: 10; }
        @keyframes hub-spoke-tilt { 0%,100%{transform:scale(1) rotate(0deg);} 50%{transform:scale(1.06) rotate(-2deg);} }
        .hub-pulse { position:absolute; top:50%; left:50%; width:180px; height:180px; margin-left:-90px; margin-top:-90px; border-radius:50%; border:2px solid rgba(220,38,38,0.35); pointer-events:none; z-index:3; animation: hub-pulse-anim 3.8s cubic-bezier(0.22,0.61,0.36,1) infinite; }
        .hub-pulse-1 { animation-delay: 0s; } .hub-pulse-2 { animation-delay: 1.9s; }
        @keyframes hub-pulse-anim { 0%{transform:scale(0.6);opacity:0.8;} 100%{transform:scale(2.2);opacity:0;} }
        @media (prefers-reduced-motion: reduce) {
          .hub-ring,.hub-glow,.hub-center,.hub-spoke-wrap,.hub-spoke,.hub-pulse { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

/* ============================ HERO ============================ */
function Hero() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("agency@zaroorijini.com");
  const [password, setPassword] = useState("Agency@12345");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (user) nav("/dashboard", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in — redirecting…");
      nav("/dashboard", { replace: true });
    } catch (e2) {
      const msg = formatApiErrorDetail(e2.response?.data?.detail) || "Login failed. Check credentials.";
      setErr(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <section
      id="hero"
      data-testid="hero"
      style={{
        background: `linear-gradient(180deg, ${CREAM} 0%, #fff 100%)`,
        padding: "56px 24px 88px",
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <div
        className="hero-grid"
        style={{
          maxWidth: 1280, margin: "0 auto",
          display: "grid", gridTemplateColumns: "1.05fr 0.85fr 0.85fr",
          gap: 40, alignItems: "center",
        }}
      >
        <div>
          <Chip>AI-Native · API-First · Multi-Tenant</Chip>
          <h1
            style={{
              fontSize: "clamp(40px, 5.6vw, 68px)", fontWeight: 800, color: INK,
              lineHeight: 1.03, letterSpacing: "-0.035em", marginTop: 22,
            }}
          >
            One Platform.<br />
            Every Order.<br />
            <span style={{ color: RED }}>Every Marketplace.</span><br />
            Powered by AI.
          </h1>
          <p style={{ color: MUTED, fontSize: 16, lineHeight: 1.65, maxWidth: 500, marginTop: 22 }}>
            Zaroori Jini is the AI-native, API-first Order Management System unifying orders,
            inventory, warehouses, shipping, marketplaces, finance &amp; automation on one platform.
          </p>

          <div style={{ display: "flex", gap: 28, marginTop: 26, flexWrap: "wrap" }}>
            {[
              { icon: <Globe size={20} color={RED} />, title: "40+ Marketplaces", sub: "Connected natively" },
              { icon: <TrendingUp size={20} color={RED} />, title: "Real-time Sync", sub: "Inventory · Orders · Tracking" },
              { icon: <Sparkles size={20} color={RED} />, title: "AI-Powered", sub: "Automation & Insights" },
            ].map((h) => (
              <div key={h.title} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: "#FFF5F5", border: `1px solid ${RED}22`, display: "grid", placeItems: "center" }}>
                  {h.icon}
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: INK, fontSize: 13 }}>{h.title}</div>
                  <div style={{ fontSize: 11, color: MUTED }}>{h.sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 12, marginTop: 30, flexWrap: "wrap" }}>
            <PrimaryButton as="link" to="/demo" testid="hero-book-demo">
              Book a Demo <ArrowRight size={16} />
            </PrimaryButton>
            <GhostButton
              onClick={() => document.getElementById("pricing-preview")?.scrollIntoView({ behavior: "smooth" })}
              testid="hero-start-trial"
            >
              Start Free Trial
              <span
                style={{
                  marginLeft: 6, fontSize: 9, fontWeight: 800,
                  background: "#FEF3C7", color: "#92400E",
                  padding: "2px 6px", borderRadius: 999, letterSpacing: "0.08em",
                }}
              >
                COMING SOON
              </span>
            </GhostButton>
          </div>
        </div>

        <div className="hub-wrapper"><MarketplaceHub /></div>

        <div
          data-testid="landing-login-card"
          style={{
            background: "#fff", borderRadius: 16,
            boxShadow: "0 24px 60px -20px rgba(15,23,42,0.15)",
            border: `1px solid ${BORDER}`, padding: 26,
          }}
        >
          <div style={{ textAlign: "center", marginBottom: 18 }}>
            <div style={{ fontWeight: 800, fontSize: 20, color: INK }}>Welcome Back</div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 3 }}>Sign in to your OMS workspace</div>
          </div>

          <form onSubmit={submit} data-testid="landing-login-form">
            <FieldLabel>Email Address</FieldLabel>
            <FieldInput
              icon={<Mail size={14} color={MUTED} />}
              type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" required
              testid="landing-email"
            />
            <div style={{ height: 12 }} />
            <FieldLabel>Password</FieldLabel>
            <FieldInput
              icon={<Lock size={14} color={MUTED} />}
              trailing={
                <button
                  type="button" onClick={() => setShowPwd((s) => !s)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: MUTED }}
                  data-testid="landing-show-pwd"
                >
                  {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              }
              type={showPwd ? "text" : "password"} value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password" required
              testid="landing-password"
            />

            {err && (
              <div
                style={{
                  marginTop: 12, padding: "8px 10px", background: "#FEF2F2",
                  border: "1px solid #FECACA", color: "#B91C1C",
                  fontSize: 12, borderRadius: 6,
                }}
                data-testid="landing-login-error"
              >
                {err}
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <PrimaryButton type="submit" full disabled={loading} testid="landing-login-submit">
                {loading ? "Signing in…" : "Sign In"}
              </PrimaryButton>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0", color: MUTED, fontSize: 11 }}>
              <div style={{ flex: 1, height: 1, background: BORDER }} />or<div style={{ flex: 1, height: 1, background: BORDER }} />
            </div>

            <button
              type="button" onClick={googleSignIn} data-testid="google-signin-btn"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                width: "100%", padding: "10px 16px", background: "#fff",
                border: `1px solid ${BORDER}`, borderRadius: 8, cursor: "pointer",
                fontSize: 14, fontWeight: 600, color: INK,
                transition: "border-color 120ms, box-shadow 120ms",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#4285F4"; e.currentTarget.style.boxShadow = "0 4px 14px -6px rgba(66,133,244,0.35)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.boxShadow = "none"; }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </button>

            <div style={{ marginTop: 14, fontSize: 11, color: MUTED, textAlign: "center", lineHeight: 1.5 }}>
              Demo: <span style={{ fontFamily: "monospace", color: INK }}>agency@zaroorijini.com</span>{" · "}
              <span style={{ fontFamily: "monospace", color: INK }}>Agency@12345</span>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        @media (max-width: 1280px) {
          .hero-grid { grid-template-columns: 1.1fr 0.9fr !important; }
          .hub-wrapper { display: none; }
        }
        @media (max-width: 900px) {
          .hero-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

/* ============================ TRUSTED BRANDS STRIP ============================ */
function TrustedBrands() {
  const badges = [
    { label: "amazon", color: "#131A22", accent: "#FF9900" },
    { label: "Flipkart", color: "#2874F0", accent: "#F0B90B" },
    { label: "Myntra", color: "#F13AB1" },
    { label: "AJIO", color: "#000" },
    { label: "Nykaa", color: "#FC2779" },
    { label: "Shopify", color: "#95BF47" },
    { label: "blinkit", color: "#0C831F" },
    { label: "Zepto", color: "#9333EA" },
    { label: "TATA CLiQ", color: "#DA1F26" },
    { label: "Meesho", color: "#F43397" },
    { label: "BigBasket", color: "#84C441" },
    { label: "JioMart", color: "#0768B1" },
  ];
  return (
    <section
      data-testid="trusted-brands"
      style={{ background: "#fff", padding: "36px 24px 40px", borderBottom: `1px solid ${BORDER}` }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto", textAlign: "center" }}>
        <div style={{ color: MUTED, fontSize: 13, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 22 }}>
          Trusted by 4,000+ brands across every leading marketplace
        </div>
        <div className="brands-marquee" style={{ overflow: "hidden", position: "relative" }}>
          <div className="brands-track" style={{ display: "flex", gap: 48, alignItems: "center", whiteSpace: "nowrap" }}>
            {[...badges, ...badges].map((b, i) => (
              <span
                key={`${b.label}-${i}`}
                style={{
                  color: b.color, fontWeight: 800, fontSize: 22,
                  letterSpacing: b.label === "AJIO" ? "0.06em" : "0",
                  fontStyle: ["amazon", "Flipkart", "Nykaa", "Myntra", "blinkit", "Meesho"].includes(b.label) ? "italic" : "normal",
                  fontFamily: b.label === "Nykaa" ? "Georgia, serif" : "inherit",
                  opacity: 0.85, flexShrink: 0,
                }}
              >
                {b.label}
              </span>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        .brands-track { animation: marquee 40s linear infinite; }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @media (prefers-reduced-motion: reduce) { .brands-track { animation: none; } }
      `}</style>
    </section>
  );
}

/* ============================ PRODUCTS (16 cards) ============================ */
const PRODUCTS = [
  { icon: <ShoppingCart />, title: "AI Order Management",   desc: "Unified order pipeline across all marketplaces & channels." },
  { icon: <Boxes />,        title: "Inventory Management",  desc: "Real-time multi-warehouse inventory sync, zero oversell." },
  { icon: <Warehouse />,    title: "Warehouse Management",  desc: "Bins, picklists, putaway, batch, packing & dispatch." },
  { icon: <RotateCcw />,    title: "Returns & Exchange",    desc: "Handle RTO, refunds & exchanges with automation." },
  { icon: <Truck />,        title: "Shipping & Logistics",  desc: "Label print, AWB, tracking across 20+ couriers." },
  { icon: <Radio />,        title: "Courier AI",            desc: "Auto-pick the fastest, cheapest courier per pincode." },
  { icon: <BarChart3 />,    title: "Analytics & BI",        desc: "Sales, profit & marketplace dashboards in real-time." },
  { icon: <DollarSign />,   title: "Finance",               desc: "Settlement recon, GST, commissions, claim tracking." },
  { icon: <FileText />,     title: "PIM",                   desc: "One catalog, every marketplace — attributes & assets." },
  { icon: <MessageSquare />,title: "CRM",                   desc: "Tickets, WhatsApp, SLA, customer 360." },
  { icon: <Bot />,          title: "Automation",            desc: "No-code rules, triggers & workflows for every op." },
  { icon: <GitBranch />,    title: "Workflow Engine",       desc: "Visual, drag-and-drop workflows across modules." },
  { icon: <Package />,      title: "Purchase Management",   desc: "POs, GRN, vendor payouts, indent to receipt." },
  { icon: <Layers />,       title: "Reconciliation",        desc: "Automated settlement reconciliation & claims." },
  { icon: <KeyRound />,     title: "Developer APIs",        desc: "REST, webhooks, SDKs, OpenAPI, sandbox." },
  { icon: <Brain />,        title: "AI Command Center",     desc: "Predict demand, detect anomalies, natural-language ops." },
];

function ProductsGrid() {
  return (
    <Section id="products" style={{ background: "#fff", paddingBottom: 56 }}>
      <SectionTitle
        eyebrow="Products"
        title="Everything You Need to Run E-Commerce at Scale"
        description="16 tightly-integrated modules — every workflow your business needs, on one AI platform."
      />
      <div
        className="products-grid"
        style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18 }}
      >
        {PRODUCTS.map((p) => (
          <div
            key={p.title}
            data-testid={`product-card-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
            style={{
              background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12,
              padding: 22, transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = "0 20px 40px -20px rgba(15,23,42,0.18)";
              e.currentTarget.style.borderColor = `${RED}55`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.borderColor = BORDER;
            }}
          >
            <div
              style={{
                width: 44, height: 44, borderRadius: 10, background: "#FFF5F5",
                color: RED, display: "grid", placeItems: "center", marginBottom: 14,
              }}
            >
              {p.icon}
            </div>
            <div style={{ fontWeight: 800, fontSize: 15, color: INK }}>{p.title}</div>
            <div style={{ fontSize: 13, color: MUTED, marginTop: 6, lineHeight: 1.55 }}>{p.desc}</div>
          </div>
        ))}
      </div>
      <style>{`
        @media (max-width: 1080px) { .products-grid { grid-template-columns: repeat(3, 1fr) !important; } }
        @media (max-width: 760px)  { .products-grid { grid-template-columns: repeat(2, 1fr) !important; } }
      `}</style>
    </Section>
  );
}

/* ============================ INTEGRATIONS (categorized with details) ============================ */
const INTEGRATIONS = [
  {
    cat: "Marketplaces",
    icon: <Store />,
    items: ["Amazon", "Flipkart", "Myntra", "AJIO", "Nykaa", "Tata CliQ", "Meesho", "FirstCry", "JioMart", "Pepperfry", "Snapdeal", "Shopsy"],
    features: ["Order sync", "Inventory push", "Listings", "Returns", "Settlements"],
    sync: "Every 60 sec",
  },
  {
    cat: "Quick Commerce",
    icon: <Zap />,
    items: ["Blinkit", "Zepto", "Swiggy Instamart", "BigBasket", "BB Now"],
    features: ["Live inventory", "Dark-store mapping", "Order routing"],
    sync: "Real-time",
  },
  {
    cat: "Websites & D2C",
    icon: <Globe />,
    items: ["Shopify", "WooCommerce", "Magento", "BigCommerce", "OpenCart", "Custom APIs"],
    features: ["Bi-directional sync", "Multi-store", "Custom fields"],
    sync: "Real-time webhooks",
  },
  {
    cat: "Couriers",
    icon: <Truck />,
    items: ["Shiprocket", "Delhivery", "Blue Dart", "Xpressbees", "DTDC", "DHL", "FedEx", "Aramex", "NimbusPost", "India Post"],
    features: ["Auto AWB", "Label print", "Live tracking", "COD reconciliation"],
    sync: "Instant",
  },
  {
    cat: "ERPs & Accounting",
    icon: <Building2 />,
    items: ["Tally", "Busy", "Zoho Books", "SAP", "Oracle", "Microsoft Dynamics"],
    features: ["Invoice export", "GST reports", "Ledger sync"],
    sync: "Scheduled + on-demand",
  },
  {
    cat: "Payments",
    icon: <DollarSign />,
    items: ["Razorpay", "Cashfree", "PayU", "Stripe"],
    features: ["Payment links", "Refunds", "Reconciliation"],
    sync: "Real-time",
  },
  {
    cat: "Beauty Platforms",
    icon: <Sparkle />,
    items: ["Nykaa Beauty", "Purplle", "Tira", "Smytten", "Kindlife"],
    features: ["Beauty catalog fields", "Batch & expiry", "Sample tracking"],
    sync: "Every 60 sec",
  },
  {
    cat: "International",
    icon: <Ship />,
    items: ["Noon UAE", "Namshi", "Amazon UAE", "Amazon USA", "Shopify Global"],
    features: ["Multi-currency", "HS-code mapping", "Duty & tax"],
    sync: "Every 60 sec",
  },
];

function IntegrationsSection() {
  return (
    <Section id="integrations" style={{ background: CREAM }}>
      <SectionTitle
        eyebrow="Integrations"
        title="290+ Marketplaces, Couriers, ERPs & Payments"
        description="Every integration is production-grade — with live sync, error handling, and self-healing retries."
      />
      <div
        className="integrations-grid"
        style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20 }}
      >
        {INTEGRATIONS.map((g) => (
          <div
            key={g.cat}
            data-testid={`integration-grid-${g.cat.toLowerCase().replace(/\s+/g, "-")}`}
            style={{
              background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14,
              padding: 24,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <div
                style={{
                  width: 40, height: 40, borderRadius: 10, background: "#FFF5F5",
                  color: RED, display: "grid", placeItems: "center",
                }}
              >
                {g.icon}
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: INK }}>{g.cat}</div>
                <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>Sync · <b style={{ color: INK }}>{g.sync}</b></div>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {g.items.map((i) => (
                <span
                  key={i}
                  style={{
                    padding: "5px 10px", background: CREAM,
                    border: `1px solid ${BORDER}`, borderRadius: 999,
                    fontSize: 12, fontWeight: 600, color: INK,
                  }}
                >
                  {i}
                </span>
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
              {g.features.map((f) => (
                <span
                  key={f}
                  style={{
                    fontSize: 11, fontWeight: 600, color: RED,
                    display: "inline-flex", alignItems: "center", gap: 4,
                  }}
                >
                  <CheckCircle2 size={12} /> {f}
                </span>
              ))}
            </div>
            <a
              href="/contact-sales"
              style={{ fontSize: 13, color: RED, fontWeight: 700, textDecoration: "none" }}
            >
              Learn More →
            </a>
          </div>
        ))}
      </div>
      <div style={{ textAlign: "center", marginTop: 32 }}>
        <span
          style={{
            padding: "8px 18px", background: "#fff", border: `1px solid ${BORDER}`,
            borderRadius: 999, fontSize: 13, fontWeight: 700, color: INK,
          }}
        >
          + 200 more integrations available on request
        </span>
      </div>
      <style>{`
        @media (max-width: 900px) { .integrations-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </Section>
  );
}

/* ============================ INDUSTRIES ============================ */
const INDUSTRIES = [
  { icon: <Shirt />,      label: "Fashion" },
  { icon: <Sparkle />,    label: "Beauty" },
  { icon: <Gem />,        label: "Jewellery" },
  { icon: <ShoppingBag />,label: "Footwear" },
  { icon: <Heart />,      label: "Healthcare" },
  { icon: <Cpu />,        label: "Electronics" },
  { icon: <Hammer />,     label: "Furniture" },
  { icon: <Factory />,    label: "Manufacturing" },
  { icon: <Store />,      label: "Retail" },
  { icon: <TruckI />,     label: "Wholesale" },
  { icon: <Zap />,        label: "Quick Commerce" },
  { icon: <Utensils />,   label: "Food" },
  { icon: <Package />,    label: "FMCG" },
  { icon: <Ship />,       label: "Export" },
  { icon: <Handshake />,  label: "Agencies" },
];

function IndustriesSection() {
  return (
    <Section id="solutions" style={{ background: "#fff" }}>
      <SectionTitle
        eyebrow="Industry Solutions"
        title="Built for Every E-Commerce Vertical"
        description="Pre-configured workflows, category attributes, and marketplace mappings for 15 industries."
      />
      <div
        className="industry-grid"
        style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14 }}
      >
        {INDUSTRIES.map((ind) => (
          <div
            key={ind.label}
            data-testid={`industry-card-${ind.label.toLowerCase().replace(/\s+/g, "-")}`}
            style={{
              background: CREAM, border: `1px solid ${BORDER}`, borderRadius: 12,
              padding: "22px 16px", textAlign: "center",
              transition: "transform 160ms ease, border-color 160ms ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.borderColor = `${RED}55`; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = BORDER; }}
          >
            <div
              style={{
                width: 42, height: 42, borderRadius: "50%",
                background: "#fff", color: RED, display: "grid",
                placeItems: "center", margin: "0 auto 10px",
                border: `1px solid ${BORDER}`,
              }}
            >
              {ind.icon}
            </div>
            <div style={{ fontWeight: 700, fontSize: 13, color: INK }}>{ind.label}</div>
          </div>
        ))}
      </div>
      <style>{`
        @media (max-width: 900px) { .industry-grid { grid-template-columns: repeat(3, 1fr) !important; } }
        @media (max-width: 560px) { .industry-grid { grid-template-columns: repeat(2, 1fr) !important; } }
      `}</style>
    </Section>
  );
}

/* ============================ AI COMMAND CENTER ============================ */
function AICommandCenter() {
  const capabilities = [
    { icon: <TrendingUp />, title: "Predict Demand",       desc: "Forecast SKU-level demand with 90%+ accuracy — never oversell again." },
    { icon: <Boxes />,      title: "Auto Allocate Inventory", desc: "AI intelligently splits stock across marketplaces & warehouses." },
    { icon: <Radio />,      title: "Detect Anomalies",     desc: "Real-time alerts on stockouts, price drops, sync failures, RTOs." },
    { icon: <Sparkles />,   title: "AI Recommendations",   desc: "Purchase, pricing, and promotional actions your ops should take today." },
    { icon: <LineChart />,  title: "Business Health",      desc: "One score tracking growth, ops efficiency, and marketplace performance." },
    { icon: <MessageSquare />, title: "Natural Language Search", desc: "Ask “top RTO SKUs in Delhi last week?” — get instant answers." },
  ];
  return (
    <Section
      id="ai"
      style={{
        background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
        color: "#fff", padding: "88px 24px",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <div
          style={{
            color: "#FCA5A5", fontWeight: 700, fontSize: 12,
            letterSpacing: "0.14em", textTransform: "uppercase",
          }}
        >
          AI Command Center
        </div>
        <h2
          style={{
            fontSize: "clamp(30px, 4.2vw, 48px)", fontWeight: 800, color: "#fff",
            marginTop: 12, letterSpacing: "-0.025em", lineHeight: 1.08,
          }}
        >
          Let AI Run Operations While<br />
          <span style={{ color: "#F87171" }}>You Focus on Growth.</span>
        </h2>
        <p style={{ maxWidth: 640, margin: "16px auto 0", color: "#CBD5E1", fontSize: 16, lineHeight: 1.6 }}>
          The command center learns your business, predicts what&apos;s next, and executes
          repetitive workflows autonomously across every module.
        </p>
      </div>

      <div
        className="ai-grid"
        style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}
      >
        {capabilities.map((c) => (
          <div
            key={c.title}
            data-testid={`ai-capability-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
            style={{
              padding: 22, background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14,
              transition: "background 160ms ease, border-color 160ms ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(220,38,38,0.12)"; e.currentTarget.style.borderColor = "rgba(220,38,38,0.4)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
          >
            <div
              style={{
                width: 44, height: 44, borderRadius: 10,
                background: "rgba(220,38,38,0.2)", color: "#F87171",
                display: "grid", placeItems: "center", marginBottom: 14,
              }}
            >
              {c.icon}
            </div>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#fff" }}>{c.title}</div>
            <div style={{ fontSize: 13, color: "#CBD5E1", marginTop: 8, lineHeight: 1.6 }}>{c.desc}</div>
          </div>
        ))}
      </div>
      <style>{`
        @media (max-width: 960px) { .ai-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 560px) { .ai-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </Section>
  );
}

/* ============================ ENTERPRISE FEATURES ============================ */
const ENTERPRISE = [
  { icon: <ShieldCheck />, title: "Role-Based Access", desc: "Granular permissions per module, team & warehouse." },
  { icon: <Layers />,      title: "Multi-Tenant",       desc: "Strict data isolation for agencies & franchisees." },
  { icon: <KeyRound />,    title: "SSO & SAML",         desc: "Enterprise SSO with Okta, Azure AD, Google Workspace." },
  { icon: <FileText />,    title: "Audit Logs",         desc: "Immutable audit trail on every action, exportable to SIEM." },
  { icon: <Boxes />,       title: "Unlimited Warehouses",desc: "Add as many warehouses, dark stores & 3PL nodes as you need." },
  { icon: <Users />,       title: "Unlimited Users",    desc: "Grow your ops team without per-seat pricing surprises." },
  { icon: <Rocket />,      title: "API & SDK",          desc: "REST + Webhooks + OpenAPI + Sandbox + SDKs (Node/Python)." },
  { icon: <ShieldCheck />, title: "Security & Compliance", desc: "SOC-ready, encryption at rest & in transit, backups & DR." },
];

function EnterpriseFeatures() {
  return (
    <Section id="enterprise" style={{ background: "#fff" }}>
      <SectionTitle
        eyebrow="Enterprise-Grade"
        title="Built for Serious Commerce Operations"
        description="Security, scale, and compliance controls trusted by enterprise brands and agencies."
      />
      <div
        className="enterprise-grid"
        style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18 }}
      >
        {ENTERPRISE.map((e) => (
          <div
            key={e.title}
            data-testid={`enterprise-feature-${e.title.toLowerCase().replace(/\s+/g, "-")}`}
            style={{
              background: CREAM, border: `1px solid ${BORDER}`, borderRadius: 12,
              padding: 22,
            }}
          >
            <div
              style={{
                width: 40, height: 40, borderRadius: 10, background: "#fff",
                color: RED, display: "grid", placeItems: "center",
                marginBottom: 14, border: `1px solid ${BORDER}`,
              }}
            >
              {e.icon}
            </div>
            <div style={{ fontWeight: 800, fontSize: 15, color: INK }}>{e.title}</div>
            <div style={{ fontSize: 13, color: MUTED, marginTop: 6, lineHeight: 1.55 }}>{e.desc}</div>
          </div>
        ))}
      </div>
      <style>{`
        @media (max-width: 1080px) { .enterprise-grid { grid-template-columns: repeat(2, 1fr) !important; } }
      `}</style>
    </Section>
  );
}

/* ============================ TESTIMONIALS ============================ */
function Testimonials() {
  const items = [
    { quote: "Reduced manual operations by over 70% within three months of implementation.", who: "Head of Ops", brand: "Fashion Brand", where: "Delhi" },
    { quote: "Inventory sync across 9 marketplaces became completely automatic. Zero oversell in Q1.", who: "COO", brand: "Beauty Brand", where: "Mumbai" },
    { quote: "Customer support response time dropped from 12 hours to under 30 minutes.", who: "CX Lead", brand: "Lifestyle Brand", where: "Bengaluru" },
    { quote: "Onboarded 42 new brands on Zaroori Jini in a quarter — the multi-tenant model is a game changer for us.", who: "Founder", brand: "Marketplace Agency", where: "Gurugram" },
  ];
  return (
    <Section id="customers" style={{ background: CREAM }}>
      <SectionTitle
        eyebrow="Customer Stories"
        title="Loved by Brands That Take Operations Seriously"
      />
      <div
        className="testi-grid"
        style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20 }}
      >
        {items.map((t) => (
          <div
            key={t.quote}
            data-testid="testimonial-card"
            style={{
              background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14,
              padding: 26,
            }}
          >
            <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
              {[0,1,2,3,4].map((i) => <Star key={i} size={14} fill={RED} color={RED} />)}
            </div>
            <div style={{ fontSize: 17, color: INK, fontWeight: 600, lineHeight: 1.5 }}>
              “{t.quote}”
            </div>
            <div
              style={{
                marginTop: 20, paddingTop: 16, borderTop: `1px dashed ${BORDER}`,
                fontSize: 13, color: MUTED,
              }}
            >
              <b style={{ color: INK }}>{t.who}</b> · {t.brand} · {t.where}
            </div>
          </div>
        ))}
      </div>
      <style>{`
        @media (max-width: 900px) { .testi-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </Section>
  );
}

/* ============================ PRICING PREVIEW ============================ */
function PricingPreview() {
  return (
    <Section id="pricing-preview" style={{ background: "#fff" }}>
      <SectionTitle
        eyebrow="Pricing"
        title="Simple, Transparent, Credit-Based"
        description="1 Credit = 1 Order Processed. Every plan includes all modules, marketplaces, AI, and support."
      />
      <div
        className="pricing-preview-grid"
        style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18 }}
      >
        {PLANS.map((p) => (
          <div
            key={p.key}
            data-testid={`pricing-preview-${p.key}`}
            style={{
              position: "relative", background: p.popular ? "#0F172A" : "#fff",
              color: p.popular ? "#fff" : INK,
              border: p.popular ? `1px solid ${RED}` : `1px solid ${BORDER}`,
              borderRadius: 14, padding: "26px 22px",
              boxShadow: p.popular ? "0 24px 60px -20px rgba(220,38,38,0.35)" : "none",
            }}
          >
            {p.popular && (
              <div
                style={{
                  position: "absolute", top: -12, right: 18,
                  background: RED, color: "#fff", padding: "4px 10px",
                  borderRadius: 999, fontSize: 10, fontWeight: 800,
                  letterSpacing: "0.14em", textTransform: "uppercase",
                }}
              >
                Most Popular
              </div>
            )}
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: p.popular ? "#F87171" : RED }}>
              {p.ideal}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 8, color: p.popular ? "#fff" : INK }}>
              {p.name}
            </div>
            <div style={{ display: "baseline", marginTop: 12 }}>
              <span style={{ fontSize: 36, fontWeight: 800, letterSpacing: "-0.02em" }}>₹{p.price.toLocaleString("en-IN")}</span>
              <span style={{ fontSize: 13, color: p.popular ? "#CBD5E1" : MUTED, marginLeft: 6 }}>
                / {p.validityMonths} mo
              </span>
            </div>
            <div style={{ fontSize: 12, color: p.popular ? "#CBD5E1" : MUTED, marginTop: 4 }}>
              ₹{p.costPerOrder}/order · {p.credits.toLocaleString("en-IN")} credits
            </div>
            <div style={{ marginTop: 22 }}>
              {p.key === "enterprise" ? (
                <PrimaryButton as="link" to="/contact-sales" full testid={`pricing-preview-cta-${p.key}`}>
                  Talk to Sales
                </PrimaryButton>
              ) : (
                <PrimaryButton as="link" to="/login" full testid={`pricing-preview-cta-${p.key}`}>
                  Get Started
                </PrimaryButton>
              )}
            </div>
          </div>
        ))}
      </div>
      <div style={{ textAlign: "center", marginTop: 32 }}>
        <GhostButton as="link" to="/pricing" testid="see-full-pricing">
          See Full Pricing & ROI Calculator <ArrowRight size={14} />
        </GhostButton>
      </div>
      <style>{`
        @media (max-width: 1080px) { .pricing-preview-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 560px)  { .pricing-preview-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </Section>
  );
}

/* ============================ FAQ ============================ */
const FAQS = [
  { q: "How does credit-based pricing work?", a: "1 Credit = 1 order processed. Every plan includes a credit pool for its validity period. Unused credits carry forward for the plan's duration. Add-on credit packs are available anytime." },
  { q: "Do you charge extra for marketplace integrations?", a: "No. All 40+ marketplace, courier, ERP, and payment integrations are included in every plan. There are no per-integration or per-channel fees." },
  { q: "Is inventory sync truly real-time?", a: "Yes. We push updates via webhooks and marketplace APIs within seconds. For channels that don't support webhooks, we poll every 60 seconds." },
  { q: "Can I bring my own courier accounts?", a: "Absolutely. Plug in your existing Shiprocket, Delhivery, Blue Dart, or other accounts. We also offer aggregated rates for brands without existing partnerships." },
  { q: "How does multi-tenant work for agencies?", a: "Agencies get a parent workspace with unlimited client sub-workspaces — each with strict data isolation, its own users, and its own marketplace credentials." },
  { q: "Is my data secure?", a: "Yes. We use encryption at rest and in transit, tenant-level isolation, immutable audit logs, backups, and SOC-ready controls. Enterprise SSO with SAML available on the Enterprise plan." },
  { q: "What if I exceed my credits?", a: "You can buy an add-on credit pack in-app (5,000 / 10,000 / 25,000+) or auto top-up. There's no downtime — orders continue processing." },
  { q: "Do you offer a free trial?", a: "A free trial is coming soon. Meanwhile, you can book a personalized demo and we'll set up a sandbox tenant with your actual marketplaces." },
  { q: "Can I migrate from Unicommerce or EasyEcom?", a: "Yes. Our onboarding team runs a guided migration — SKUs, marketplace mappings, historical orders, and rules — typically completed within a week." },
  { q: "Does Zaroori Jini support B2B and D2C orders?", a: "Yes. Universal Order Engine handles marketplace, D2C, POS, B2B, and international orders in one unified pipeline." },
  { q: "Do you support Quick Commerce (Blinkit, Zepto, Instamart)?", a: "Yes — with native integrations, dark-store mapping, and live inventory push tuned for QC SLAs." },
  { q: "What integrations are included with couriers?", a: "AWB generation, label printing, shipping manifests, live tracking, NDR management, COD reconciliation, and return pickup — across 20+ courier partners." },
  { q: "Can I automate operations without writing code?", a: "Yes. The Automation Engine supports 100+ triggers and actions with no-code rules. A visual drag-and-drop rule builder is on the roadmap." },
  { q: "How does AI improve my operations?", a: "AI predicts demand, allocates inventory across warehouses, picks the best courier per pincode, forecasts stock-outs, flags anomalies, and auto-responds to customer tickets." },
  { q: "Do you offer white-label OMS?", a: "Yes. Enterprise and Agency plans support white-label branding — your logo, your colors, and your domain." },
  { q: "What kind of support do you provide?", a: "Every plan includes email support, training, knowledge base, and community. Scale and Enterprise plans include dedicated onboarding and priority support." },
  { q: "Do you have public APIs and SDKs?", a: "Yes. REST APIs, Webhooks, OpenAPI docs, sandbox, and Node/Python SDKs are available. Developer portal launching soon." },
  { q: "Can I run multiple warehouses and brands?", a: "All plans support unlimited warehouses and unlimited brands. Multi-warehouse allocation is AI-driven by default." },
  { q: "How long does onboarding take?", a: "Typical onboarding: 3-7 business days for standard setups. Enterprise migrations from other OMS platforms complete within 2-3 weeks." },
  { q: "Which industries do you serve?", a: "Fashion, Beauty, Jewellery, Footwear, Healthcare, Electronics, Furniture, Manufacturing, Retail, Wholesale, Quick Commerce, Food, FMCG, Export, and Marketplace Agencies." },
];

function FAQItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={testid}
      style={{
        borderBottom: `1px solid ${BORDER}`, padding: "18px 0",
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        type="button"
        style={{
          background: "none", border: "none", cursor: "pointer",
          padding: 0, width: "100%", display: "flex",
          justifyContent: "space-between", alignItems: "center",
          textAlign: "left", color: INK,
        }}
        aria-expanded={open}
      >
        <span style={{ fontWeight: 700, fontSize: 16 }}>{q}</span>
        <ChevronDown
          size={20}
          color={MUTED}
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 200ms ease" }}
        />
      </button>
      {open && (
        <div style={{ marginTop: 12, fontSize: 14, color: MUTED, lineHeight: 1.7 }}>
          {a}
        </div>
      )}
    </div>
  );
}

function FAQSection() {
  return (
    <Section id="resources" style={{ background: "#fff" }}>
      <SectionTitle eyebrow="FAQ" title="Answers to Common Questions" />
      <div
        style={{ maxWidth: 860, margin: "0 auto" }}
        data-testid="faq-list"
      >
        {FAQS.map((f, i) => <FAQItem key={f.q} q={f.q} a={f.a} testid={`faq-item-${i}`} />)}
      </div>
    </Section>
  );
}

/* ============================ FINAL CTA ============================ */
function FinalCTA() {
  return (
    <Section
      id="company"
      style={{
        background: `linear-gradient(135deg, ${RED} 0%, ${RED_DARK} 100%)`,
        color: "#fff", padding: "80px 24px",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 800, letterSpacing: "-0.02em" }}>
          Ready to Unify Your Commerce Operations?
        </h2>
        <p
          style={{
            maxWidth: 640, margin: "18px auto 0",
            fontSize: 16, opacity: 0.92, lineHeight: 1.6,
          }}
        >
          Join thousands of brands using Zaroori Jini OMS to automate ops,
          improve customer experience, and accelerate marketplace growth.
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 32, flexWrap: "wrap" }}>
          <PrimaryButton
            as="link" to="/demo"
            testid="cta-book-demo"
          >
            Book a Live Demo <ArrowRight size={16} />
          </PrimaryButton>
          <a
            href="/contact-sales"
            data-testid="cta-schedule-consult"
            style={{
              padding: "13px 26px", background: "transparent", color: "#fff",
              borderRadius: 8, border: "1.5px solid rgba(255,255,255,0.6)",
              fontWeight: 700, fontSize: 14, textDecoration: "none",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
          >
            Schedule Consultation
          </a>
        </div>
      </div>
    </Section>
  );
}

/* ============================ ROOT ============================ */
export default function Landing() {
  return (
    <div
      data-testid="landing-page"
      style={{
        background: "#fff",
        color: INK,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
        minHeight: "100vh",
      }}
    >
      <MarketingHeader />
      <Hero />
      <TrustedBrands />
      <ProductsGrid />
      <IntegrationsSection />
      <IndustriesSection />
      <AICommandCenter />
      <EnterpriseFeatures />
      <Testimonials />
      <PricingPreview />
      <FAQSection />
      <FinalCTA />
      <MarketingFooter />
    </div>
  );
}
